import { supabase } from "../auth";
import { resizeForUpload } from "./imageResize";
import { createPhoto } from "../db/photos";
import type { PhotoRow } from "@garden/types/db";

/* Photo upload pipeline (web side).
 *
 * 1. Resize the user-selected file (canvas, 1920px max edge, JPEG q=0.85).
 * 2. POST it to the Worker's /photos/upload endpoint with the user's
 *    Supabase JWT in the Authorization header. The Worker verifies the JWT,
 *    streams the body to R2 under `${userId}/${uuid}.jpg`, and returns the
 *    r2_key.
 * 3. Insert a row in `photo` from the browser. RLS handles ownership; the
 *    Worker doesn't have the service-role key (and shouldn't).
 *
 * The "Worker proxies the file to R2" choice (vs presigned URLs) is in
 * decisions/0017-photo-upload-via-worker-proxy.md.
 */

const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined) ??
  "https://garden-app-worker.tom-sandifordnam.workers.dev";

export interface UploadedPhoto {
  row: PhotoRow;
  resizedBytes: number;
}

export async function uploadPhotoFile(
  file: File,
  gardenId: string,
): Promise<UploadedPhoto> {
  if (!supabase) throw new Error("Supabase is not configured.");
  if (!gardenId) throw new Error("uploadPhotoFile: gardenId is required");

  const resized = await resizeForUpload(file);

  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) throw new Error("Not authenticated");

  const res = await fetch(`${WORKER_URL}/photos/upload`, {
    method: "POST",
    headers: {
      "content-type": resized.type,
      authorization: `Bearer ${token}`,
      "x-image-width": String(resized.width),
      "x-image-height": String(resized.height),
    },
    body: resized.blob,
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`Upload failed (${res.status}): ${txt || res.statusText}`);
  }

  const body = (await res.json()) as { r2_key: string };
  if (!body.r2_key) throw new Error("Worker response missing r2_key");

  const today = new Date().toISOString().slice(0, 10);
  const row = await createPhoto(supabase, {
    garden_id: gardenId,
    r2_key: body.r2_key,
    width_px: resized.width,
    height_px: resized.height,
    taken_date: today,
  });

  return { row, resizedBytes: resized.blob.size };
}
